import type { Um } from './units.js';

/**
 * Cabinet-local coordinate system (right-handed):
 *   +X → right (cabinet width), +Y → up (height), +Z → toward the viewer (front).
 * Origin: left / back / floor corner of the cabinet footprint.
 * The carcass starts at y = plinthHeight (+ legsHeight).
 */
export type Axis = 'x' | 'y' | 'z';
export type Vec3 = [Um, Um, Um];

export type Direction = 'left' | 'right' | 'bottom' | 'top' | 'back' | 'front';

/**
 * Local edges of a rectangular panel (see docs/edge-convention.md):
 *   L1 — edge parallel to LENGTH at width = 0
 *   L2 — edge parallel to LENGTH at width = W
 *   W1 — edge parallel to WIDTH  at length = 0
 *   W2 — edge parallel to WIDTH  at length = L
 * Banding length of L-edges is the panel length, of W-edges the panel width.
 */
export type EdgeId = 'L1' | 'L2' | 'W1' | 'W2';
export const EDGE_IDS: readonly EdgeId[] = ['L1', 'L2', 'W1', 'W2'];

export type PanelRole =
  | 'side'
  | 'bottom'
  | 'top'
  | 'back'
  | 'shelf'
  | 'divider'
  | 'door'
  | 'drawer-front'
  | 'drawer-side'
  | 'drawer-back'
  | 'drawer-bottom'
  | 'plinth'
  | 'custom';

export type MaterialSlot = 'carcass' | 'back' | 'front' | 'drawer-box' | 'drawer-bottom' | 'plinth';

export type GrainDirection = 'length' | 'width' | 'none';

export interface PanelEdge {
  id: EdgeId;
  /** Banding length of this edge, finished size. */
  length: Um;
  /** Direction in cabinet-local space the edge face points to. */
  facing: Direction;
  /** Parts whose faces touch this edge face (within contact tolerance) and the covered area fraction 0..1. */
  contacts: EdgeContact[];
}

export interface EdgeContact {
  partId: string;
  fraction: number;
  /** Gap between the edge face and the other part's face, µm (≤ contact tolerance ⇒ touching). */
  gap: Um;
}

export interface Connection {
  /** Other part id. */
  partId: string;
  kind: 'butt' | 'groove' | 'rebate' | 'overlay' | 'support';
  /** Which local edge of this part participates, if any. */
  edge?: EdgeId;
}

export interface PanelPart {
  /** Stable id: `${cabinetId}/${localKey}`. localKey is stable across parameter changes. */
  id: string;
  localKey: string;
  cabinetId: string;
  role: PanelRole;
  label: string;
  /** Finished dimensions (after edge banding). */
  length: Um;
  width: Um;
  thickness: Um;
  materialSlot: MaterialSlot;
  grain: GrainDirection;
  /** Axis-aligned placement in cabinet-local space. */
  placement: {
    min: Vec3;
    lengthAxis: Axis;
    widthAxis: Axis;
    thicknessAxis: Axis;
  };
  edges: Record<EdgeId, PanelEdge>;
  connections: Connection[];
  /** True for parts that move in opening preview (doors, drawers). */
  movable?: 'hinge-left' | 'hinge-right' | 'slide';
  /** Parts that enter the cut list. Hardware/decor are not PanelParts. */
  inCutList: boolean;
  generic?: boolean;
  /** Irregular outline (CNC): rectangles relative to the part's min corner in its length/width plane (x = width axis, y = length axis of a front). */
  cnc?: { outline: { x0: Um; y0: Um; x1: Um; y1: Um }[] };
  /** Hinge side of a front. */
  hingeSide?: 'left' | 'right' | 'top' | 'bottom';
}

export type IssueSeverity = 'error' | 'warning' | 'info';

export interface Issue {
  severity: IssueSeverity;
  code: string;
  message: string;
  cabinetId?: string;
  partId?: string;
  param?: string;
  /** The other part of a collision. */
  otherPartId?: string;
  /** The other element of an overlap between elements. */
  otherCabinetId?: string;
  /** Edge of `partId` the issue is about. */
  edge?: string;
  /** Link id (link issues) or edge override key (`edge.override-orphaned`). */
  ref?: string;
}

export interface HardwareItem {
  id: string;
  cabinetId: string;
  kind: 'hinge' | 'slide' | 'handle' | 'leg' | 'shelf-support' | 'connector';
  label: string;
  quantity: number;
  generic: boolean;
  note?: string;
}

export interface CabinetResult {
  cabinetId: string;
  parts: PanelPart[];
  hardware: HardwareItem[];
  issues: Issue[];
  /** Overall outer size including plinth/legs/fronts. */
  bounds: { size: Vec3 };
}
